import type {
  MessageOperationSubmission,
  PreviewAnnotationPayload,
  ThreadActionSelection,
} from "@afar-research/contracts";
import type { BrainComposerCollection } from "@afar-research/afar";
import { create } from "zustand";

import type { ComposerSubmissionIntent } from "./composer-logic";
import type { ComposerFileAttachment, ComposerImageAttachment } from "./composerDraftStore";
import type { TerminalContextDraft } from "./lib/terminalContext";
import { randomUUID } from "./lib/utils";
import type { ReviewCommentContext } from "./reviewCommentContext";

/**
 * A composer submission held back while the thread's turn is running. It
 * carries the full draft snapshot so the send path can dispatch it later with
 * the same text, attachments, and contexts the user pressed Enter on.
 */
export interface QueuedComposerMessage {
  id: string;
  prompt: string;
  images: ComposerImageAttachment[];
  files: ComposerFileAttachment[];
  terminalContexts: TerminalContextDraft[];
  previewAnnotations: PreviewAnnotationPayload[];
  reviewComments: ReviewCommentContext[];
  /**
   * The Design Operation this send *is*, when it is one. A Design send is its
   * operation rather than its text, so the operation has to wait in the queue
   * with the message. Rebuilding it from the composer on release cannot work:
   * the composer was cleared when the message was queued, and by then may hold
   * a different send entirely.
   */
  operation?: MessageOperationSubmission;
  /**
   * The Action chip and what a Brain Action gathered, at queue time.
   *
   * Composer state, snapshotted the way the attachments above it are, because
   * queuing consumes the composer. The queue holds what the user provided and
   * the send arranges it on dispatch, as it does for an ordinary message.
   * Arranging before queuing instead would put App-authored text in the queued
   * bubble and show the user words they never typed.
   */
  action?: ThreadActionSelection;
  brainCollection?: BrainComposerCollection;
  submissionIntent: ComposerSubmissionIntent;
  /**
   * Set when the message was created by Stop or a failed restore, not by the
   * user pressing send. It waits for Send now instead of leaving on its own.
   */
  holdUntilUserAction?: boolean;
  createdAt: string;
}

interface QueuedMessageStoreState {
  queuesByThreadKey: Record<string, QueuedComposerMessage[]>;
  /**
   * Bumped by `drain`. A send that took a message before a drain and finishes
   * its upload after it compares this to the value it captured and gives up,
   * so Stop cannot be followed by a queued message starting a new turn.
   */
  drainGeneration: number;
  enqueue: (threadKey: string, message: Omit<QueuedComposerMessage, "id">) => QueuedComposerMessage;
  /**
   * Removes one message and returns it, or null when another caller already
   * claimed it. Nothing behind it moves: the next message waits for the thread
   * to go idle again, which the turn a claimed send starts is about to prevent.
   */
  remove: (threadKey: string, id: string) => QueuedComposerMessage | null;
  /**
   * Puts a message back at the head, held for user action. Used when its
   * send failed: the queue keeps its order and nothing behind it overtakes.
   */
  holdAtFront: (threadKey: string, message: QueuedComposerMessage) => void;
  /** Removes and returns every queued message for the thread, oldest first. */
  drain: (threadKey: string) => QueuedComposerMessage[];
}

const EMPTY_QUEUE: QueuedComposerMessage[] = [];

/** In-memory only: a queued message is a live intent, not a draft worth persisting. */
export const useQueuedMessageStore = create<QueuedMessageStoreState>()((set, get) => ({
  queuesByThreadKey: {},
  drainGeneration: 0,
  enqueue: (threadKey, message) => {
    const entry: QueuedComposerMessage = { ...message, id: randomUUID() };
    set((state) => ({
      queuesByThreadKey: {
        ...state.queuesByThreadKey,
        [threadKey]: [...(state.queuesByThreadKey[threadKey] ?? EMPTY_QUEUE), entry],
      },
    }));
    return entry;
  },
  remove: (threadKey, id) => {
    const queue = get().queuesByThreadKey[threadKey];
    const entry = queue?.find((message) => message.id === id);
    if (!queue || !entry) {
      return null;
    }
    set((state) => {
      const remaining = (state.queuesByThreadKey[threadKey] ?? EMPTY_QUEUE).filter(
        (message) => message.id !== id,
      );
      const queuesByThreadKey = { ...state.queuesByThreadKey };
      if (remaining.length === 0) {
        delete queuesByThreadKey[threadKey];
      } else {
        queuesByThreadKey[threadKey] = remaining;
      }
      return { queuesByThreadKey };
    });
    return entry;
  },
  holdAtFront: (threadKey, message) => {
    set((state) => {
      const rest = (state.queuesByThreadKey[threadKey] ?? EMPTY_QUEUE).filter(
        (entry) => entry.id !== message.id,
      );
      return {
        queuesByThreadKey: {
          ...state.queuesByThreadKey,
          [threadKey]: [{ ...message, holdUntilUserAction: true }, ...rest],
        },
      };
    });
  },
  drain: (threadKey) => {
    const queue = get().queuesByThreadKey[threadKey];
    if (!queue || queue.length === 0) {
      return EMPTY_QUEUE;
    }
    set((state) => {
      const queuesByThreadKey = { ...state.queuesByThreadKey };
      delete queuesByThreadKey[threadKey];
      return { queuesByThreadKey, drainGeneration: state.drainGeneration + 1 };
    });
    return queue;
  },
}));

/**
 * A queued message waits for the thread to go idle.
 *
 * Releasing mid-turn — at a tool boundary, as this once did — lands the
 * follow-up inside the running turn, where the Agent Provider folds it into
 * work the user has not seen the end of. Waiting for idle means the message
 * starts its own turn against the transcript the user was actually reading.
 * "connecting" is the gap between a send and the provider picking it up, so
 * nothing is due there either.
 */
export function isQueuedMessageDue(input: {
  message: Pick<QueuedComposerMessage, "holdUntilUserAction">;
  phase: "connecting" | "running" | "ready" | "disconnected";
}): boolean {
  if (input.message.holdUntilUserAction) return false;
  return input.phase !== "connecting" && input.phase !== "running";
}

export function useQueuedMessages(threadKey: string): QueuedComposerMessage[] {
  return useQueuedMessageStore((state) => state.queuesByThreadKey[threadKey] ?? EMPTY_QUEUE);
}

/**
 * The send context for replaying a queued message.
 *
 * A queued message carries its own snapshot of what the user pressed Enter on.
 * The composer was cleared when it was queued, so replaying it has to send
 * that snapshot rather than whatever the composer holds now — which is empty,
 * or worse, a newer message the user has since typed.
 */
export function queuedMessageSendContext<T>(context: T, message: QueuedComposerMessage) {
  return {
    ...context,
    prompt: message.prompt,
    images: message.images,
    files: message.files,
    terminalContexts: message.terminalContexts,
    previewAnnotations: message.previewAnnotations,
    reviewComments: message.reviewComments,
    ...(message.brainCollection !== undefined ? { brainCollection: message.brainCollection } : {}),
  };
}

/**
 * Splits a restored message's attachments at the composer's per-turn cap.
 *
 * The draft store drops attachments over the cap silently and revokes their
 * preview URLs with them, so handing it more than fits loses the user's
 * images outright. The overflow goes back into the queue instead, held for
 * the user, and images are preferred over path attachments because an image
 * is bytes the composer is the only remaining copy of.
 */
export function planQueuedAttachmentRestore(input: {
  images: ReadonlyArray<ComposerImageAttachment>;
  files: ReadonlyArray<ComposerFileAttachment>;
  room: number;
}): {
  images: ComposerImageAttachment[];
  files: ComposerFileAttachment[];
  overflowImages: ComposerImageAttachment[];
  overflowFiles: ComposerFileAttachment[];
} {
  const room = Math.max(0, input.room);
  const imageCount = Math.min(room, input.images.length);
  const fileCount = Math.min(room - imageCount, input.files.length);
  return {
    images: input.images.slice(0, imageCount),
    files: input.files.slice(0, fileCount),
    overflowImages: input.images.slice(imageCount),
    overflowFiles: input.files.slice(fileCount),
  };
}
